import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } } };
const fade = { hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.25, 1, 0.5, 1] } } };

const tracks = [
  { title: 'Midnight City', artist: 'M83', bpm: '105', key: 'Am', ok: true },
  { title: 'Strobe', artist: 'Deadmau5', bpm: '128', key: 'F#m', ok: true },
  { title: 'Levels', artist: 'Avicii', bpm: '126', key: 'Em', ok: false },
  { title: 'Titanium', artist: 'David Guetta', bpm: '126', key: 'Ebm', ok: true },
  { title: 'Clarity', artist: 'Zedd', bpm: '128', key: 'Gb', ok: false },
];

export default function Hero() {
  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-glow" aria-hidden="true" />
      <div className="hero-grid-bg" aria-hidden="true" />

      <div className="container">
        <motion.div variants={stagger} initial="hidden" animate="show" className="hero-copy">
          <motion.div variants={fade} className="hero-badge">
            <span className="hero-badge-dot" aria-hidden="true" />
            <span className="hero-badge-text">Early access · macOS, Windows & Linux</span>
          </motion.div>

          <motion.h1 variants={fade} data-testid="hero-title" className="hero-title">
            Run your DJ library<br className="hero-title-br" />
            {' '}with one desktop app
          </motion.h1>

          <motion.p variants={fade} data-testid="hero-description" className="hero-lead">
            Bonk combines Rekordbox DB sync, Quick Tag, PathFlo, JourneyFlo, analytics, benchmarking, and watch-folder automation in one fast workflow.
          </motion.p>

          <motion.div variants={fade} className="hero-buttons">
            <a
              className="btn btn-primary hero-btn"
              href="#contact"
              data-testid="hero-download-btn"
              onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            >
              Join Waitlist <ArrowRight size={15} aria-hidden="true" />
            </a>
            <a
              className="btn btn-secondary hero-secondary-btn"
              href="#features"
              data-testid="hero-features-btn"
              onClick={(e) => { e.preventDefault(); document.querySelector('#features')?.scrollIntoView({ behavior: 'smooth' }); }}
            >
              See Features
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.25, 1, 0.5, 1] }}
          className="hero-mockup-wrap"
        >
          <div className="hero-mockup-card" data-testid="hero-app-mockup">
            <div className="hero-mockup-inner">
              <div className="hero-mockup-chrome">
                <span className="hero-mockup-dot" aria-hidden="true" />
                <span className="hero-mockup-dot" aria-hidden="true" />
                <span className="hero-mockup-dot" aria-hidden="true" />
                <span className="hero-mockup-chrome-path">bonk ~ library/all-tracks</span>
              </div>
              <table className="hero-mockup-table">
                <thead>
                  <tr>
                    {['', 'Title', 'Artist', 'BPM', 'Key', 'Status'].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tracks.map((t) => (
                    <tr key={t.title}>
                      <td className="col-status">
                        <div className={`status-dot ${t.ok ? 'status-dot--ok' : 'status-dot--warn'}`} aria-hidden="true" />
                      </td>
                      <td className="track-title">{t.title}</td>
                      <td className="track-artist">{t.artist}</td>
                      <td className="track-mono">{t.bpm}</td>
                      <td className="track-key">{t.key}</td>
                      <td>
                        <span className={`status-pill ${t.ok ? 'status-pill--ok' : 'status-pill--warn'}`}>
                          {t.ok ? 'Clean' : 'Needs Fix'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="hero-trust-row"
        >
          {['Rekordbox 6 & 7 sync', 'Quick Tag workflows', 'PathFlo + JourneyFlo', 'Watch Folder automation'].map((b) => (
            <span key={b} className="hero-trust-badge">
              <span className="hero-trust-dot" aria-hidden="true" />{b}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
